import { useRef } from 'react'

/**
 * Returns pointer handlers that fire onLongPress(x, y) after a hold on touch devices.
 * Desktop mouse events are ignored — right-click contextmenu handles those.
 * Cancels if the pointer moves more than 8px before the delay expires.
 * Blocks the subsequent click event so the row-open handler doesn't fire.
 */
export function useLongPress(onLongPress, delay = 500) {
  const timer = useRef(null)
  const pos = useRef(null)

  function start(e) {
    if (e.pointerType === 'mouse') return
    pos.current = { x: e.clientX, y: e.clientY }
    timer.current = setTimeout(() => {
      // Haptic feedback on supported devices
      if (navigator.vibrate) navigator.vibrate(40)
      // Block the tap-end click from propagating to the row's onClick
      const blocker = (ev) => {
        ev.stopPropagation()
        ev.preventDefault()
        document.removeEventListener('click', blocker, true)
      }
      document.addEventListener('click', blocker, true)
      onLongPress(pos.current.x, pos.current.y)
    }, delay)
  }

  function cancel() {
    clearTimeout(timer.current)
    timer.current = null
  }

  function move(e) {
    if (!pos.current) return
    if (Math.abs(e.clientX - pos.current.x) > 8 || Math.abs(e.clientY - pos.current.y) > 8) cancel()
  }

  return {
    onPointerDown: start,
    onPointerUp: cancel,
    onPointerMove: move,
    onPointerCancel: cancel,
  }
}
